import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';

import { onboardingDoneAtom, onboardingOpenAtom } from 'atoms/onboardingAtom';
import { ONBOARDING_STEPS } from './steps';

// E2E バイパスビルドでは初回自動表示を抑止する（既存 beless spec を阻害しないため）。
// 本番ビルドではフラグ未設定のため従来どおり初回自動表示する。再生はどちらでもヘルプから可能。
const IS_E2E = process.env.REACT_APP_E2E_BYPASS_AUTH === 'true';

interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
}

const SPOT_PAD = 8; // スポットライト枠の対象まわりの余白
const CARD_W = 320;

// 主要 UI を順に説明する自前 coachmark ツアー（no-dep）。表示のみで操作対象の挙動は変えない（C1）。
// 対象要素を box-shadow の巨大スプレッドで切り抜き風にハイライトし、説明カードを近傍に表示する。
export const OnboardingTour: React.FC = () => {
    const [open, setOpen] = useAtom(onboardingOpenAtom);
    const done = useAtomValue(onboardingDoneAtom);
    const setDone = useSetAtom(onboardingDoneAtom);
    const [step, setStep] = useState(0);
    const [rect, setRect] = useState<Rect | null>(null);
    const autoStarted = useRef(false);

    // 初回のみ自動表示（完了/スキップ済みなら出さない）。E2E では抑止。
    useEffect(() => {
        if (!IS_E2E && !autoStarted.current && !done) {
            autoStarted.current = true;
            setOpen(true);
        }
    }, [done, setOpen]);

    // 開くたびに先頭ステップへ戻す。
    useEffect(() => {
        if (open) setStep(0);
    }, [open]);

    // 対象要素の矩形を測定する。step / open / リサイズで再計算し、未検出なら中央表示。
    useEffect(() => {
        if (!open) return;
        const measure = () => {
            const selector = ONBOARDING_STEPS[step]?.selector;
            const el = selector ? document.querySelector(selector) : null;
            if (el) {
                const r = el.getBoundingClientRect();
                setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
            } else {
                setRect(null);
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [open, step]);

    const finish = useCallback(() => {
        setDone(true);
        setOpen(false);
    }, [setDone, setOpen]);

    // Esc でツアーを閉じる（スキップ相当）。
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') finish();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, finish]);

    if (!open) return null;

    const current = ONBOARDING_STEPS[step];
    const isLast = step >= ONBOARDING_STEPS.length - 1;

    // 説明カードの配置: 対象の下に十分な余地があれば下、なければ上。未検出時は画面中央。
    const cardSx = (() => {
        if (!rect) {
            return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' } as const;
        }
        const placeBelow = window.innerHeight - (rect.top + rect.height) > 220;
        const top = placeBelow ? rect.top + rect.height + SPOT_PAD + 8 : undefined;
        const bottom = placeBelow ? undefined : window.innerHeight - rect.top + SPOT_PAD + 8;
        const left = Math.max(16, Math.min(rect.left, window.innerWidth - CARD_W - 16));
        return { top, bottom, left } as const;
    })();

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: (t) => t.zIndex.modal + 1 }}>
            {/* クリック遮断レイヤ。対象なし時は全面ディム（対象あり時はスポット枠の影でディムする）。 */}
            <Box
                onClick={() => {}}
                sx={{ position: 'fixed', inset: 0, bgcolor: rect ? 'transparent' : 'rgba(0,0,0,0.5)' }}
            />

            {/* スポットライト枠（box-shadow の巨大スプレッドで対象以外をディム）。 */}
            {rect && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: rect.top - SPOT_PAD,
                        left: rect.left - SPOT_PAD,
                        width: rect.width + SPOT_PAD * 2,
                        height: rect.height + SPOT_PAD * 2,
                        borderRadius: 1.5,
                        border: '2px solid',
                        borderColor: 'primary.main',
                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                        pointerEvents: 'none',
                    }}
                />
            )}

            {/* 説明カード。 */}
            <Card
                role="dialog"
                aria-modal="true"
                aria-label="操作ガイド"
                sx={{
                    position: 'fixed',
                    width: CARD_W,
                    maxWidth: 'calc(100vw - 32px)',
                    pointerEvents: 'auto',
                    ...cardSx,
                }}
            >
                <CardContent>
                    <Typography variant="overline" color="text.secondary">
                        {step + 1} / {ONBOARDING_STEPS.length}
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                        {current.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {current.body}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                        <Button size="small" color="inherit" onClick={finish}>
                            スキップ
                        </Button>
                        <Stack direction="row" spacing={1}>
                            {step > 0 && (
                                <Button size="small" onClick={() => setStep((s) => s - 1)}>
                                    戻る
                                </Button>
                            )}
                            {isLast ? (
                                <Button size="small" variant="contained" onClick={finish}>
                                    完了
                                </Button>
                            ) : (
                                <Button size="small" variant="contained" onClick={() => setStep((s) => s + 1)}>
                                    次へ
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};
